import { useState, useEffect, Suspense } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router";
import WelcomeChecklist from "./WelcomeChecklist";
import { subscribe } from "../engine/consoleNav";
import { SCREENS, pathForScreen } from "../engine/consoleScreens";

/**
 * ConsoleShell — Customer Console shell & navigation.
 *
 * Renders the operational console nav and content for a customer/tenant
 * principal. With F05/F06-A shipped the console is live end-to-end: Submit Job
 * mounts the real SubmitForm (via SubmitTab), Jobs mounts the shared JobsView
 * in its console variant (list -> detail with lifecycle, Adam/Eve reasoning,
 * outputs, logs, cancel), Billing mounts the live usage/quota screen
 * (BillingTab), and Account mounts the live profile / password / API-key screen
 * (AccountTab, F07).
 *
 * Registration is not a console screen — registration lives at the auth gate
 * (F02) since the user is already authenticated here. Monitor and Results from
 * F04 collapse into the single Jobs screen (F05 FR8).
 *
 * ROUTING (ADR 0009). This is a LAYOUT ROUTE: App mounts it with one child route
 * per screen, it renders the chrome and an <Outlet/>, and the active screen is the
 * URL rather than a `useState`. The auth guard lives on the layout route in App,
 * which is why nothing here checks a session — a shell that renders at all has
 * already been admitted.
 *
 * The nav entries are real <a> elements (NavLink), not buttons. A control that
 * changes the URL should be a link, so cmd-click, middle-click and "copy link
 * address" work; that is most of what deep-linkable URLs are worth. The e2e and
 * unit suites query them by `role: "link"` accordingly.
 *
 * E2E contract: data-testid="console-view" on the root, screen nav links by
 * visible label, LOGOUT button.
 */
export default function ConsoleShell({ user, displayName, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();

  // Bumped on every consoleNav request. It keys the screen container so the target
  // REMOUNTS and consumes its one-shot prefill even when the requested screen is
  // already active — e.g. the F12 onboarding "Submit your first job" fires while
  // Submit (the default screen) is showing, so navigating to /submit from /submit
  // would be a no-op and SubmitTab's mount-only consumePrefill() would never run.
  //
  // It survived the router for exactly that reason: a router makes the URL change
  // remount the screen, but a request to the screen you are already on changes no
  // URL. Ordinary nav clicks do NOT bump it and keep their screen state.
  const [navNonce, setNavNonce] = useState(0);

  // The consoleNav channel (ADR 0001) lets a zero-prop surface — the Guide, the F12
  // onboarding checklist, a job detail — ask the shell to switch screens and hand the
  // target a one-shot prefill. ADR 0009 changed only what the shell DOES with that
  // request: it navigates instead of calling setScreen. All six callers are unchanged
  // and tabs stay zero-prop, which is the contract ADR 0001 was protecting.
  useEffect(
    () =>
      subscribe((screenId) => {
        navigate(pathForScreen(screenId));
        setNavNonce((n) => n + 1);
      }),
    [navigate],
  );

  // The screen the URL is on, for the Suspense fallback's label only. The first path
  // segment, so /jobs/<id> still reads as Jobs.
  const segment = location.pathname.split("/")[1];
  const active = SCREENS.find((s) => s.id === segment);

  return (
    <div data-testid="console-view" className="min-h-screen bg-canvas text-fg">
      {/* HEADER */}
      <div className="bg-surface border-b border-line px-6 py-3 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-4">
          <div>
            <div className="font-semibold tracking-[0.18em] text-fg">
              OMEGA AI HPC <span className="text-fg3 font-normal tracking-normal text-sm ml-1">Console</span>
            </div>
          </div>
          <span className="text-xs bg-surface2 rounded-none px-2 py-1 text-fg2">{displayName || user || "customer"}</span>
        </div>

        <div className="flex items-center gap-1 flex-wrap">
          {SCREENS.map((s) => (
            <NavLink
              key={s.id}
              to={pathForScreen(s.id)}
              className={({ isActive }) =>
                `px-3 py-1.5 rounded-none text-sm transition-colors duration-150 cursor-pointer no-underline ${
                  isActive ? "bg-accent-tint text-accent font-medium" : "text-fg2 hover:text-fg"
                }`
              }
            >
              {s.l}
            </NavLink>
          ))}
          <span className="w-px h-5 bg-line mx-2" />
          <button
            onClick={onLogout}
            className="px-3 py-1.5 rounded-none text-sm text-fg2 border border-line hover:text-fg transition-colors duration-150 cursor-pointer"
          >
            LOGOUT
          </button>
        </div>
      </div>

      {/* FIRST-LOGIN ONBOARDING (F12) — a value-first welcome + activation
          checklist that precedes every screen. Self-fetches its gating data and
          renders nothing for a returning/onboarded or dismissed customer. */}
      <WelcomeChecklist />

      {/* SCREEN CONTENT — the matched child route.

          `key={navNonce}` remounts the subtree on a consoleNav request; see the nonce
          comment above for why a router does not make that redundant.

          The placeholder panel that stood here for "a screen without a component" is
          gone, and could not have survived: a screen is now a <Route> and a route
          without an element renders nothing to place a panel in. It was unreachable
          already — every entry in the registry has carried a component since F07. */}
      <div key={navNonce} className="px-6 py-5">
        <Suspense fallback={<div className="text-sm text-fg3 py-6">Loading {active?.l ?? "screen"}…</div>}>
          <Outlet />
        </Suspense>
      </div>

      {/* FOOTER — the product name, and nothing else.

          OMEGA-826: the right-hand span read "{n} screens | tenant <uuid> | role
          <name>". A screen count is an implementation detail, and a raw tenant
          UUID and a role name are internal identifiers that mean nothing to the
          paying customer who is the Console's only audience (ADR 0004 / E06).
          The same role and tenant values come off the Account profile under
          OMEGA-833, so the two surfaces agree.

          The `access` prop and the "Research" switch that stood here are gone: the
          Console has no RBAC and one view, so there is nothing to switch to.

          The brand string is F11 FR2's, verbatim, and is not ours to reword here:
          F11 pins "Omega AI HPC Console" in two independent places (the Product
          Definition table and the Scope bullet) and FR2 names this footer as one
          of the surfaces it governs. An earlier revision of this branch read
          "Omega AI HPC Customer Console", which left the shell rendering two
          brand forms at once against the masthead above. */}
      <div className="px-6 py-3 border-t border-line text-xs text-fg3">
        <span>Omega AI HPC Console</span>
      </div>
    </div>
  );
}
